import { api } from '@/Shared/Lib/Logic';
import { EntityService, ApiResponse } from './entity-service-interface';
import { BlogData, EntityMode, ExportParams } from '@/Shared';
import { ExportFormat } from '@/Domains';
import { AxiosRequestConfig } from 'axios';

/**
 * makeEntityService
 *
 * Factory function to create a standardized API service for a given entity.
 * This service provides a unified, type-safe interface for performing
 * CRUD operations and common fetch patterns against a REST backend.
 *
 * The generated service abstracts HTTP concerns and enforces consistent:
 * - response typing via `ApiResponse<T>`
 * - entity mode handling (`active`, `trash`, etc.)
 * - endpoint conventions across all entities
 *
 * @param baseUrl - Base API endpoint for the entity (e.g. `/users`, `/posts`)
 *
 * @returns An `EntityService<T>` exposing domain-agnostic data operations
 *
 * Author: Samano CASTRE
 * Date: January 2026
 */
export function makeEntityService<T>(baseUrl: string, exportPrefix? : string, exportDomain? : string) {
	async function blobData(params: ExportParams<any>, format: ExportFormat, exportType: string): Promise<BlogData> {
		if(!exportPrefix || !exportDomain) {
			throw new Error("Domain " + baseUrl + " is not exportable.");
		}
		const { data, headers } = await api.post<Blob>(
			`${exportPrefix}/${exportType}/${exportDomain}`,
			{ ...params, format, exportType },
			{ responseType: 'blob' }
		);

		// Attempt to extract the filename from the Content-Disposition header
		const disposition = headers['content-disposition'];
		const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
		const filename = match ? match[1].replace(/['"]/g, '') : null;

		// Return the binary file and its optional filename
		return {
			blob: data,
			filename
		};
	}
	return {

		/**
		 * Fetch all entities for a given mode.
		 *
		 * Defaults to `'active'` mode when none is provided.
		 * The mode is forwarded as a query parameter to allow
		 * backend-side filtering.
		 *
		 * @param mode - Optional entity mode (`active`, `trash`, ...)
		 * @returns A promise resolving to a typed API response containing a list of entities
		 */
		async fetchAll(mode?: EntityMode): Promise<ApiResponse<T[]>> {
			const { data } = await api.get<ApiResponse<T[]>>(`${baseUrl}/?mode=${mode ?? 'active'}`);
			return data;
		},

		/**
		 * Fetch a single entity by its identifier.
		 *
		 * Supports entity modes to allow retrieving items from
		 * alternative states (e.g. trashed entities).
		 *
		 * @param id - Unique identifier of the entity
		 * @param mode - Optional entity mode (`active`, `trash`, ...)
		 * @returns A promise resolving to a typed API response containing the entity
		 */
		async fetchOne(id: string | number): Promise<ApiResponse<T>> {
			const { data } = await api.get<ApiResponse<T>>(`${baseUrl}/${id}`);
			return data;
		},

		/**
		 * Fetch all entities updated since a given date.
		 *
		 * Intended for incremental synchronization use cases
		 * (e.g. background refresh, cache rehydration).
		 *
		 * @param since - ISO date string used as a lower bound for updates
		 * @returns A promise resolving to a typed API response containing updated entities
		 */
		async fetchSinceDate(since: string): Promise<ApiResponse<T[]>> {
			const { data } = await api.get<ApiResponse<T[]>>(`${baseUrl}/touch-since/${since}`);
			return data;
		},

		/**
		 * Create a new entity.
		 *
		 * Accepts a partial payload to allow backend-side defaults
		 * and computed fields.
		 *
		 * @param payload - Partial entity data to persist
		 * @returns A promise resolving to a typed API response containing the created entity
		 */
		async create(payload: Partial<T> | FormData, idempotencyKey? : string): Promise<ApiResponse<T>> {
			const { data } = await api.post<ApiResponse<T>>(`${baseUrl}`, payload, {idempotencyKey});
			return data;
		},


		/**
		 * Update an existing entity.
		 *
		 * Supports both JSON payloads and `FormData`:
		 * - When using `FormData`, a `_method=PATCH` override is applied
		 *   to accommodate backends that do not support PATCH with multipart data.
		 * - Otherwise, a standard PATCH request is used.
		 *
		 * @param id - Unique identifier of the entity
		 * @param payload - Updated entity data or multipart form data
		 * @returns A promise resolving to a typed API response containing the updated entity
		 */
		async update(id: string | number, payload: T | FormData): Promise<ApiResponse<T>> {
			if (payload instanceof FormData) {
				payload.append('_method', 'PATCH');
				return (await api.post<ApiResponse<T>>(`${baseUrl}/${id}`, payload)).data;
			}
			return (await api.patch<ApiResponse<T>>(`${baseUrl}/${id}`, payload)).data
		},

		async restore(id: string | number): Promise<ApiResponse<T>> {
			const { data } = await api.patch<ApiResponse<T>>(`${baseUrl}/${id}/restore`);
			return data;
		},


		/**
		 * Delete an entity by its identifier.
		 *
		 * This operation performs a hard delete unless the backend
		 * implements soft-delete semantics internally.
		 *
		 * @param id - Unique identifier of the entity
		 * @returns A promise that resolves once the deletion is complete
		 */
		async delete(id: string | number): Promise<void> {
			await api.delete(`${baseUrl}/${id}`);
		},

		async search(params: ExportParams<any>): Promise<ApiResponse<T[]>> {
			const { data } = await api.post<ApiResponse<T[]>>(`${baseUrl}/search`, params);
			return data;
		},


		async export(params: ExportParams<any>, format: ExportFormat): Promise<BlogData> {
			return blobData(params, format, 'download');
		},


		async generate(params: ExportParams<any>, format: ExportFormat): Promise<BlogData> {

			return blobData(params, format, 'generate');
		},

	} as EntityService<T>;
}
